import { useState, useEffect } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMenu, FiX, FiGlobe, FiExternalLink, FiLogOut, FiGrid, FiChevronDown } from 'react-icons/fi'
import Logo from '../brand/Logo'
import Button from '../ui/Button'
import { cn } from '../../lib/utils'
import { useAuthStore } from '../../store/authStore'
import { useAuth } from '../../hooks/useAuth'

interface InternalNavLink {
  to: string
  labelKey: string
  external?: false
  isHash?: boolean
  isNew?: boolean
}

interface ExternalNavLink {
  to: string
  labelKey: string
  external: true
  isHash?: false
  isNew?: boolean
}

type NavLinkItem = InternalNavLink | ExternalNavLink

// 상단 상시 노출 — 유입·전환 핵심 4개 (영어 라벨에서도 한 줄 유지)
const primaryLinks: NavLinkItem[] = [
  { to: '/ac-datacenter', labelKey: 'nav.acDatacenter', isNew: true },
  { to: '/insurance-refund', labelKey: 'nav.insuranceRefund', isNew: true },
  { to: '/newsletter', labelKey: 'nav.newsletter' },
  { to: '/on-premise', labelKey: 'nav.onPremise' },
]

// "More ▾" 드롭다운으로 접는 보조 메뉴
const moreLinks: NavLinkItem[] = [
  { to: '/pricing', labelKey: 'nav.pricing' },
  { to: '/blog', labelKey: 'nav.blog' },
  { to: 'https://www.youtube.com/@Heinrich_dasan/videos', labelKey: 'nav.youtube', external: true },
  { to: '/about', labelKey: 'nav.about' },
  { to: '/contact', labelKey: 'nav.contact' },
]

// 모바일은 전부 평면 노출
const navLinks: NavLinkItem[] = [...primaryLinks, ...moreLinks]

export default function Header() {
  const { t, i18n } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const { logout } = useAuth()

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30)
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isOpen])

  const toggleLang = () => {
    const nextLang = i18n.language === 'ko' ? 'en' : 'ko'
    i18n.changeLanguage(nextLang)

    const path = location.pathname
    const seoPrefixes = ['/blog', '/ac-datacenter', '/insurance-refund']

    if (path.startsWith('/en/')) {
      const remainingPath = path.substring(3)
      const isSeoRoute = seoPrefixes.some(prefix => remainingPath.startsWith(prefix))
      if (isSeoRoute) {
        navigate(remainingPath)
      }
    } else {
      const isSeoRoute = seoPrefixes.some(prefix => path.startsWith(prefix))
      if (isSeoRoute) {
        navigate('/en' + path)
      }
    }
  }

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, to: string) => {
    const hash = to.split('#')[1]
    if (!hash) return
    if (location.pathname === '/') {
      e.preventDefault()
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' })
    }
    setIsOpen(false)
  }

  // 스크롤 시 zinc-950/70 + 블러로 전환, 기본은 투명
  return (
    <header
      className={cn(
        'fixed top-0 inset-x-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-zinc-950/70 backdrop-blur-2xl border-b border-white/[0.08]'
          : 'bg-transparent',
      )}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* 로고 */}
        <Link to="/" className="shrink-0" aria-label={t('nav.aria.home', { defaultValue: 'Heinrich 홈으로' })}>
          <Logo size="md" />
        </Link>

        {/* 데스크탑 네비게이션 — 핵심 4개 + More 드롭다운 */}
        <nav className="hidden md:flex items-center gap-0.5" aria-label={t('nav.aria.main', { defaultValue: '메인 내비게이션' })}>
          {primaryLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 relative group',
                  isActive
                    ? 'text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-white/[0.06]',
                )
              }
            >
              {({ isActive }) => (
                <>
                  <span className="inline-flex items-center gap-1.5">
                    {t(link.labelKey)}
                    {link.isNew && (
                      <span
                        aria-label="New"
                        className="inline-flex items-center justify-center leading-none text-[9px] font-extrabold tracking-[0.05em] px-1.5 py-0.5 rounded-md bg-red-500 text-white uppercase shadow-[0_0_10px_-1px_rgba(239,68,68,0.7)] ring-1 ring-red-400/50 pointer-events-none"
                      >
                        NEW
                      </span>
                    )}
                  </span>
                  <span className={cn(
                    'absolute bottom-1 left-4 right-4 h-px bg-gradient-to-r from-amber-400 to-amber-400 transition-transform duration-200 origin-left',
                    isActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100',
                  )} />
                </>
              )}
            </NavLink>
          ))}

          {/* More ▾ 드롭다운 */}
          <div
            className="relative"
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
              aria-haspopup="true"
              className={cn(
                'whitespace-nowrap inline-flex items-center gap-1 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200',
                moreOpen ? 'text-white bg-white/[0.06]' : 'text-zinc-400 hover:text-white hover:bg-white/[0.06]',
              )}
            >
              {t('nav.more', { defaultValue: '더보기' })}
              <FiChevronDown size={13} className={cn('transition-transform duration-200', moreOpen && 'rotate-180')} aria-hidden="true" />
            </button>

            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-full pt-2 w-52"
                >
                  <div className="rounded-xl border border-white/[0.08] bg-zinc-950/95 backdrop-blur-2xl p-1.5 shadow-2xl">
                    {moreLinks.map((link) =>
                      link.external ? (
                        <a
                          key={link.to}
                          href={link.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setMoreOpen(false)}
                          className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                        >
                          {t(link.labelKey)}
                          <FiExternalLink size={12} className="opacity-50" aria-hidden="true" />
                        </a>
                      ) : (
                        <NavLink
                          key={link.to}
                          to={link.to}
                          onClick={() => setMoreOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              'flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                              isActive ? 'text-white bg-white/[0.06]' : 'text-zinc-400 hover:text-white hover:bg-white/[0.06]',
                            )
                          }
                        >
                          {t(link.labelKey)}
                          {link.isNew && (
                            <span
                              aria-label="New"
                              className="inline-flex items-center justify-center leading-none text-[8.5px] font-extrabold tracking-wider px-1.5 py-0.5 rounded-md bg-red-500 text-white uppercase"
                            >
                              NEW
                            </span>
                          )}
                        </NavLink>
                      ),
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </nav>

        {/* 우측 액션 */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            className="hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs text-zinc-500 hover:text-zinc-300 rounded-md hover:bg-white/[0.06] transition-all duration-200"
            aria-label={t('nav.aria.language', { defaultValue: '언어 변경' })}
          >
            <FiGlobe size={13} aria-hidden="true" />
            <span className="font-medium tracking-wider">
              {i18n.language === 'ko' ? 'EN' : 'KO'}
            </span>
          </button>

          {isAuthenticated ? (
            <>
              {/* 로그아웃 (로그인 버튼 자리) */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void logout()}
                leftIcon={<FiLogOut size={13} />}
                className="hidden md:inline-flex text-zinc-400 hover:text-white"
                title={user?.email ?? undefined}
              >
                {t('auth.logout', { defaultValue: '로그아웃' })}
              </Button>
              {/* 대시보드(사업장 관리) 이동 (무료 시작 버튼 자리) */}
              <Link to="/dashboard/site" className="hidden md:block">
                <Button size="sm" leftIcon={<FiGrid size={13} />}>
                  {t('nav.dashboard', { defaultValue: '대시보드' })}
                </Button>
              </Link>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden md:block">
                <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/signup" className="hidden md:block">
                <Button size="sm">
                  {t('nav.signup')}
                </Button>
              </Link>
            </>
          )}

          {/* 모바일 햄버거 */}
          <button
            className="md:hidden p-2 text-zinc-400 hover:text-white rounded-md hover:bg-white/[0.06] transition-colors"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? t('nav.aria.menuClose', { defaultValue: '메뉴 닫기' }) : t('nav.aria.menuOpen', { defaultValue: '메뉴 열기' })}
            aria-expanded={isOpen}
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.span
                key={isOpen ? 'close' : 'open'}
                initial={{ rotate: -90, opacity: 0 }}
                animate={{ rotate: 0, opacity: 1 }}
                exit={{ rotate: 90, opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {isOpen ? <FiX size={22} aria-hidden="true" /> : <FiMenu size={22} aria-hidden="true" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>

      {/* 모바일 메뉴 */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-zinc-950/95 backdrop-blur-2xl border-b border-white/[0.08]"
          >
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-1" aria-label={t('nav.aria.mobile', { defaultValue: '모바일 내비게이션' })}>
              {navLinks.map((link) => {
                if (link.external) {
                  return (
                    <a
                      key={link.to}
                      href={link.to}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsOpen(false)}
                      className="inline-flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors"
                    >
                      {t(link.labelKey)}
                      <FiExternalLink size={12} className="opacity-50" aria-hidden="true" />
                    </a>
                  )
                }

                if (link.isHash) {
                  return (
                    <a
                      key={link.to}
                      href={link.to}
                      onClick={(e) => handleHashClick(e, link.to)}
                      className="px-4 py-3 rounded-md text-sm font-medium text-zinc-400 hover:text-white hover:bg-white/[0.06] transition-colors cursor-pointer"
                    >
                      {t(link.labelKey)}
                    </a>
                  )
                }

                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setIsOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium transition-colors duration-200',
                        isActive
                          ? 'text-white bg-white/[0.06]'
                          : 'text-zinc-400 hover:text-white hover:bg-white/[0.04]',
                      )
                    }
                  >
                    {t(link.labelKey)}
                    {link.isNew && (
                      <span
                        aria-label="New"
                        className="inline-flex items-center justify-center text-[8.5px] font-extrabold tracking-wider px-1.5 py-0.5 rounded-full bg-gradient-to-br from-amber-400 to-red-500 text-white shadow-sm uppercase"
                      >
                        New
                      </span>
                    )}
                  </NavLink>
                )
              })}

              <div className="pt-3 flex flex-col gap-2 border-t border-white/[0.08] mt-1">
                <div className="flex items-center justify-between px-1">
                  <button
                    onClick={toggleLang}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 rounded-md hover:bg-white/[0.06] transition-colors"
                    aria-label={t('nav.aria.language', { defaultValue: '언어 변경' })}
                  >
                    <FiGlobe size={13} aria-hidden="true" />
                    <span className="font-medium tracking-wider">
                      {i18n.language === 'ko' ? 'EN' : 'KO'}
                    </span>
                  </button>
                </div>
                {isAuthenticated ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      leftIcon={<FiLogOut size={13} />}
                      onClick={() => { setIsOpen(false); void logout() }}
                    >
                      {t('auth.logout', { defaultValue: '로그아웃' })}
                    </Button>
                    <Link to="/dashboard/site" onClick={() => setIsOpen(false)}>
                      <Button size="sm" className="w-full" leftIcon={<FiGrid size={13} />}>
                        {t('nav.dashboard', { defaultValue: '대시보드' })}
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setIsOpen(false)}>
                      <Button variant="outline" size="sm" className="w-full">
                        {t('nav.login')}
                      </Button>
                    </Link>
                    <Link to="/signup" onClick={() => setIsOpen(false)}>
                      <Button size="sm" className="w-full">
                        {t('nav.signup')}
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
